import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import HandshakeIcon from '../../../components/HandshakeIcon';
import esgSupplierCommunicationService from '../../../services/esg/esgSupplierCommunicationService';
import {
  SIGNAL_CHANNELS,
  MARKETPLACE_COMMUNICATION_HUB,
  SCORE_META,
  subscribeAllSignals,
  summarizeChannel
} from '../../../services/signal/signalRegistry';

const ESGSupplierCommunicationHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [communicationData, setCommunicationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [originalPath, setOriginalPath] = useState(null);
  const [showReturnBanner, setShowReturnBanner] = useState(false);
  const [signalScores, setSignalScores] = useState({});

  useEffect(() => {
    const unsubscribe = esgSupplierCommunicationService.subscribe((data) => {
      setCommunicationData(data);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Media signal visibility — same scores the Studio signal pages and the
  // Marketplace media signal pages read, so all three stay in agreement.
  useEffect(() => subscribeAllSignals(setSignalScores), []);

  // Parse originalPath from query params if coming from preview redirect
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const from = params.get('from');
      const op = params.get('originalPath');
      if (from === 'preview' && op) {
        // Only allow internal navigation paths to avoid open redirect
        if (op.startsWith('/') && !op.startsWith('//')) {
          // Avoid showing if it's this communication hub page itself
            const currentPath = location.pathname + location.search + location.hash;
            if (op !== currentPath) {
              setOriginalPath(op);
              setShowReturnBanner(true);
            }
        }
      }
    } catch {
      // silent
    }
  }, [location.search, location.pathname, location.hash]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-gray-100 text-gray-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Compliance': return '✅';
      case 'Environmental': return '🌱';
      case 'Social': return '👥';
      case 'Governance': return '⚖️';
      case 'Onboarding': return '🚀';
      case 'Performance': return '📊';
      case 'Training': return '📚';
      case 'Collaboration': return <HandshakeIcon size={20} />;
      default: return '💬';
    }
  };

  const getEngagementColor = (level) => {
    switch (level) {
      case 'High': return 'text-green-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  // Filter channels based on current filters
  const filteredChannels = communicationData?.communicationChannels?.filter(channel => {
    const matchesSearch = !searchTerm || 
      channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      channel.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || channel.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || channel.category === filterCategory;
    
    return matchesSearch && matchesStatus && matchesCategory;
  }) || [];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Channels</p>
              <p className="text-2xl font-bold text-gray-900">{communicationData?.channelsOverview?.totalChannels}</p>
            </div>
            <div className="text-3xl">💬</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Active communication channels
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Conversations</p>
              <p className="text-2xl font-bold text-blue-600">{communicationData?.channelsOverview?.activeConversations}</p>
            </div>
            <div className="text-3xl">🗨️</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Ongoing discussions
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Collaboration Spaces</p>
              <p className="text-2xl font-bold text-purple-600">{communicationData?.channelsOverview?.collaborationSpaces}</p>
            </div>
            <HandshakeIcon size={48} />
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Active workspaces
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Satisfaction Score</p>
              <p className="text-2xl font-bold text-green-600">{communicationData?.performanceMetrics?.stakeholderSatisfaction?.overallSatisfaction}</p>
            </div>
            <div className="text-3xl">⭐</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Out of 10 points
          </div>
        </div>
      </div>

      {/* Communication Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Volume */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Volume</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Today</span>
              <div className="text-right">
                <div className="font-medium">{communicationData?.communicationAnalytics?.messageVolume?.today}</div>
                <div className="text-xs text-green-600">↗️ {communicationData?.communicationAnalytics?.messageVolume?.trend}</div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">This Week</span>
              <div className="text-right">
                <div className="font-medium">{communicationData?.communicationAnalytics?.messageVolume?.thisWeek?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Avg: {communicationData?.communicationAnalytics?.messageVolume?.averagePerDay}/day</div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">This Month</span>
              <div className="text-right">
                <div className="font-medium">{communicationData?.communicationAnalytics?.messageVolume?.thisMonth?.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Response Metrics */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Average Response Time</span>
              <div className="font-medium">{communicationData?.communicationAnalytics?.responseMetrics?.averageResponseTime}</div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">First Response Time</span>
              <div className="font-medium">{communicationData?.communicationAnalytics?.responseMetrics?.firstResponseTime}</div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Resolution Time</span>
              <div className="font-medium">{communicationData?.communicationAnalytics?.responseMetrics?.resolutionTime}</div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Satisfaction Rating</span>
              <div className="font-medium text-green-600">{communicationData?.communicationAnalytics?.responseMetrics?.satisfactionRating}/10</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stakeholder Engagement Overview */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Stakeholder Engagement Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {communicationData?.stakeholderEngagement?.stakeholderGroups?.map((group, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{group.group}</h4>
                <span className={`text-sm font-medium ${getEngagementColor(group.engagementLevel)}`}>
                  {group.engagementLevel}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Count:</span>
                  <span className="font-medium">{group.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Response Rate:</span>
                  <span className="font-medium">{group.responseRate}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Satisfaction:</span>
                  <span className="font-medium text-green-600">{group.satisfactionScore}/10</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Preferred Channel:</span>
                  <span className="text-xs">{group.preferredChannel}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
        <div className="space-y-4">
          {communicationData?.recentActivities?.slice(0, 5).map(activity => (
            <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900 truncate">{activity.title}</h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(activity.priority)}`}>
                    {activity.priority}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>{new Date(activity.timestamp).toLocaleString()}</span>
                  <span>{activity.recipients} recipients • {activity.engagement}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderChannelsTab = () => (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search channels..."
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Categories</option>
                <option value="Compliance">Compliance</option>
                <option value="Environmental">Environmental</option>
                <option value="Social">Social</option>
                <option value="Governance">Governance</option>
                <option value="Onboarding">Onboarding</option>
              </select>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Showing {filteredChannels.length} of {communicationData?.communicationChannels?.length || 0} channels
          </div>
        </div>
      </div>

      {/* Communication Channels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredChannels.map(channel => (
          <div key={channel.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedChannel(channel)}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{getCategoryIcon(channel.category)}</span>
                  <h3 className="font-medium text-gray-900 truncate">{channel.name}</h3>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(channel.status)}`}>
                  {channel.status}
                </span>
              </div>
              
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{channel.description}</p>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium">{channel.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span>{channel.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Participants:</span>
                  <span className="font-medium">{channel.participants}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Messages:</span>
                  <span>{channel.messageCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Unread:</span>
                  <span className={channel.unreadCount > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                    {channel.unreadCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Priority:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(channel.priority)}`}>
                    {channel.priority}
                  </span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  Last activity: {new Date(channel.lastActivity).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Moderators: {channel.moderators.join(', ')}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCollaborationTab = () => (
    <div className="space-y-6">
      {/* Collaboration Spaces Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{communicationData?.collaborationSpaces?.length}</div>
            <div className="text-sm text-gray-600">Active Spaces</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {communicationData?.collaborationSpaces?.reduce((sum, space) => sum + space.participants, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Participants</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {Math.round(communicationData?.collaborationSpaces?.reduce((sum, space) => sum + space.progress, 0) / communicationData?.collaborationSpaces?.length || 0)}%
            </div>
            <div className="text-sm text-gray-600">Avg Progress</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">
              {communicationData?.collaborationSpaces?.filter(space => space.status === 'Active').length}
            </div>
            <div className="text-sm text-gray-600">Active Projects</div>
          </div>
        </div>
      </div>

      {/* Collaboration Spaces */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {communicationData?.collaborationSpaces?.map(space => (
          <div key={space.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedSpace(space)}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">{space.name}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(space.status)}`}>
                  {space.status}
                </span>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">{space.description}</p>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress:</span>
                  <span className="font-medium">{space.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full" 
                    style={{ width: `${space.progress}%` }}
                  ></div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Participants:</span>
                    <div className="font-medium">{space.participants}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Category:</span>
                    <div className="font-medium">{space.category}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Lead:</span>
                    <div className="text-xs">{space.lead}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Deadline:</span>
                    <div className="text-xs">{new Date(space.deadline).toLocaleDateString()}</div>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-600 mb-2">Features Available:</div>
                  <div className="flex flex-wrap gap-1">
                    {space.features.documentSharing && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">📄 Docs</span>}
                    {space.features.taskManagement && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">✅ Tasks</span>}
                    {space.features.videoConferencing && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">🎥 Video</span>}
                    {space.features.whiteboardCollaboration && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">🖊️ Board</span>}
                    {space.features.realTimeEditing && <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">✏️ Edit</span>}
                  </div>
                </div>
                
                {space.recentActivity && space.recentActivity.length > 0 && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-600 mb-2">Recent Activity:</div>
                    <div className="text-xs text-gray-500">
                      {space.recentActivity[0].type}: {space.recentActivity[0].item} by {space.recentActivity[0].user}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAutomationTab = () => (
    <div className="space-y-6">
      {/* Automation Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{communicationData?.automatedNotifications?.length}</div>
            <div className="text-sm text-gray-600">Active Automations</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {communicationData?.automatedNotifications?.reduce((sum, notif) => sum + notif.analytics.sent, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Messages Sent</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {Math.round(communicationData?.automatedNotifications?.reduce((sum, notif) => sum + notif.analytics.responseRate, 0) / communicationData?.automatedNotifications?.length || 0)}%
            </div>
            <div className="text-sm text-gray-600">Avg Response Rate</div>
          </div>
        </div>
      </div>

      {/* Automated Notifications */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Automated Notifications</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notification</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Trigger</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Analytics</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {communicationData?.automatedNotifications?.map(notification => (
                <tr key={notification.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{notification.type}</div>
                      <div className="text-sm text-gray-500">{notification.content}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                      {notification.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{notification.frequency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(notification.nextTrigger).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(notification.status)}`}>
                      {notification.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>Sent: {notification.analytics.sent}</div>
                    <div>Open Rate: {notification.analytics.openRate}%</div>
                    <div>Response: {notification.analytics.responseRate}%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-purple-600 hover:text-purple-900 mr-3">Edit</button>
                    <button className="text-red-600 hover:text-red-900">Pause</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Message Templates */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Message Templates</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {communicationData?.messageTemplates?.map(template => (
              <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{template.name}</h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                    {template.status}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Category:</span>
                    <span>{template.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Language:</span>
                    <span>{template.language}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Usage:</span>
                    <span className="font-medium">{template.usage} times</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated:</span>
                    <span>{new Date(template.lastUpdated).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-600 mb-2">Variables:</div>
                  <div className="flex flex-wrap gap-1">
                    {template.content.variables.map(variable => (
                      <span key={variable} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex justify-between">
                  <button 
                    onClick={() => esgSupplierCommunicationService.useTemplate(template.id)}
                    className="text-xs text-purple-600 hover:text-purple-900"
                  >
                    Use Template
                  </button>
                  <button className="text-xs text-gray-600 hover:text-gray-900">Edit</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsTab = () => (
    <div className="space-y-6">
      {/* Integration Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Systems</h3>
          <div className="space-y-3">
            {Object.entries(communicationData?.integrationPlatforms?.emailSystems || {}).map(([platform, data]) => (
              <div key={platform} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${data.status === 'Connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="capitalize font-medium">{platform}</span>
                </div>
                <div className="text-sm text-gray-500">
                  {data.messagesSynced?.toLocaleString()} synced
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Collaboration Tools</h3>
          <div className="space-y-3">
            {Object.entries(communicationData?.integrationPlatforms?.collaborationTools || {}).map(([platform, data]) => (
              <div key={platform} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${data.status === 'Connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="capitalize font-medium">{platform.replace(/([A-Z])/g, ' $1')}</span>
                </div>
                <div className="text-sm text-gray-500">
                  {data.channelsManaged || data.meetingsScheduled} managed
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Platforms</h3>
          <div className="space-y-3">
            {Object.entries(communicationData?.integrationPlatforms?.documentPlatforms || {}).map(([platform, data]) => (
              <div key={platform} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${data.status === 'Connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="capitalize font-medium">{platform.replace(/([A-Z])/g, ' $1')}</span>
                </div>
                <div className="text-sm text-gray-500">
                  {data.documentsShared} shared
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance & Audit */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Compliance & Audit</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Retention Policies</h4>
            <div className="space-y-2 text-sm">
              {Object.entries(communicationData?.complianceAudit?.retentionPolicies || {}).map(([type, period]) => (
                <div key={type} className="flex justify-between">
                  <span className="text-gray-600 capitalize">{type.replace(/([A-Z])/g, ' $1')}:</span>
                  <span className="font-medium">{period}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Encryption Standards</h4>
            <div className="space-y-2 text-sm">
              {Object.entries(communicationData?.complianceAudit?.encryptionStandards || {}).map(([type, standard]) => (
                <div key={type} className="flex justify-between">
                  <span className="text-gray-600 capitalize">{type.replace(/([A-Z])/g, ' $1')}:</span>
                  <span className="font-medium">{standard}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Audit Trail */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Audit Trail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Framework</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {communicationData?.complianceAudit?.auditTrail?.slice(0, 10).map(entry => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.action}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.user}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.recipient}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.messageType || entry.documentType}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.complianceFramework}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.encryptionLevel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const signalSummaries = SIGNAL_CHANNELS.map(channel => ({
    channel,
    scores: signalScores[channel.id] || {},
    summary: summarizeChannel(channel, signalScores[channel.id] || {})
  }));

  const renderSignalsTab = () => {
    const totalDimensions = signalSummaries.reduce((sum, s) => sum + s.summary.total, 0);
    const totalScored = signalSummaries.reduce((sum, s) => sum + s.summary.scored, 0);
    const needsAttention = signalSummaries.filter(s => s.summary.overall === 'no-go').length;
    const populatedChannels = signalSummaries.filter(s => s.summary.populated).length;

    return (
      <div className="space-y-6">
        {/* Shared visibility notice */}
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-700">
              <span className="font-medium text-purple-900">Shared signal visibility.</span>{' '}
              These are the same scored dimensions surfaced on the Marketplace media signal pages.
              Scoring a dimension on either side updates this view and the Studio signal pages together.
            </div>
            <a
              href={MARKETPLACE_COMMUNICATION_HUB}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-purple-700 hover:text-purple-900 whitespace-nowrap"
            >
              View on Marketplace →
            </a>
          </div>
        </div>

        {/* Aggregate strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Signal Channels</p>
            <p className="text-2xl font-bold text-gray-900">{SIGNAL_CHANNELS.length}</p>
            <div className="mt-2 text-sm text-gray-500">AM, FM, and Ubuntu</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Channels Populated</p>
            <p className="text-2xl font-bold text-blue-600">{populatedChannels}/{SIGNAL_CHANNELS.length}</p>
            <div className="mt-2 text-sm text-gray-500">At least one dimension scored</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Dimensions Scored</p>
            <p className="text-2xl font-bold text-purple-600">{totalScored}/{totalDimensions}</p>
            <div className="mt-2 text-sm text-gray-500">Across all channels</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Needs Attention</p>
            <p className={`text-2xl font-bold ${needsAttention > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {needsAttention}
            </p>
            <div className="mt-2 text-sm text-gray-500">
              {needsAttention > 0 ? 'Channels reporting no-go' : 'No channels flagged'}
            </div>
          </div>
        </div>

        {/* Per-channel detail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {signalSummaries.map(({ channel, scores, summary }) => {
            const meta = summary.overall ? SCORE_META[summary.overall] : null;
            return (
              <div key={channel.id} className="bg-white rounded-lg shadow flex flex-col">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold" style={{ color: channel.heading }}>
                      {channel.marketplaceLabel}
                    </h3>
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={
                        meta
                          ? { background: meta.surface, color: meta.tone }
                          : { background: '#f3f4f6', color: '#6b7280' }
                      }
                    >
                      {meta ? meta.label : 'Unscored'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{channel.description}</p>

                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Population</span>
                      <span>{summary.scored} of {summary.total} scored</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${summary.completionPct}%`, background: channel.accent }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex-1">
                  <div className="space-y-2">
                    {channel.dimensions.map(dim => {
                      const value = scores[dim.id];
                      const dimMeta = value ? SCORE_META[value] : null;
                      return (
                        <div key={dim.id} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-700 truncate" title={dim.desc}>{dim.label}</span>
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                            style={
                              dimMeta
                                ? { background: dimMeta.surface, color: dimMeta.tone }
                                : { background: '#f3f4f6', color: '#9ca3af' }
                            }
                          >
                            {dimMeta ? dimMeta.label : 'Unscored'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <Link
                    to={channel.studioPath}
                    className="text-xs font-medium hover:underline"
                    style={{ color: channel.accent }}
                  >
                    Score in Studio →
                  </Link>
                  <a
                    href={channel.marketplacePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-gray-600 hover:text-gray-900"
                  >
                    Marketplace ↗
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Communication Overview', icon: '📊' },
    { id: 'channels', label: 'Communication Channels', icon: '💬' },
    { id: 'signals', label: 'Media Signals', icon: '📡' },
    { id: 'collaboration', label: 'Collaboration Spaces', icon: 'handshake' },
    { id: 'automation', label: 'Automation & Templates', icon: '🤖' },
    { id: 'integrations', label: 'Integrations & Compliance', icon: '🔗' }
  ];

  return (
    <div className="space-y-6">
      {showReturnBanner && originalPath && (
        <div className="flex items-center justify-between p-4 rounded-md border border-purple-300 bg-purple-50 text-sm">
          <div>
            You were redirected here from the preview site. You can return to your originally requested page.
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(originalPath, { replace: true })}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-medium hover:bg-purple-700 transition"
            >
              Return to Previous Page
            </button>
            <button
              onClick={() => setShowReturnBanner(false)}
              className="text-purple-700 hover:text-purple-900 text-xs font-medium"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">Communication Hub</h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white text-purple-700">
                ★ FEATURED APP
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white border border-white/40">
                PLUG &amp; PLAY
              </span>
            </div>
            <p className="text-purple-100 mt-2 max-w-2xl">
              Unified operational and communications layer for stakeholder engagement, collaboration,
              and compliance communications — shared across Studio and KRO Marketplace.
            </p>
            <a
              href={MARKETPLACE_COMMUNICATION_HUB}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-md bg-white text-purple-700 text-sm font-medium hover:bg-purple-50 transition"
            >
              Open on KRO Marketplace ↗
            </a>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{communicationData?.performanceMetrics?.stakeholderSatisfaction?.overallSatisfaction || 0}</div>
            <div className="text-purple-100">Satisfaction Score</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2 inline-flex align-middle">
                  {tab.icon === 'handshake' ? <HandshakeIcon size={16} /> : tab.icon}
                </span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'channels' && renderChannelsTab()}
          {activeTab === 'signals' && renderSignalsTab()}
          {activeTab === 'collaboration' && renderCollaborationTab()}
          {activeTab === 'automation' && renderAutomationTab()}
          {activeTab === 'integrations' && renderIntegrationsTab()}
        </div>
      </div>

      {/* Channel Detail Modal */}
      {selectedChannel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Channel Details: {selectedChannel.name}</h3>
              <button
                onClick={() => setSelectedChannel(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Channel Information</h4>
                  <div className="mt-2 space-y-2 text-sm">
                    <div><span className="font-medium">Type:</span> {selectedChannel.type}</div>
                    <div><span className="font-medium">Category:</span> {selectedChannel.category}</div>
                    <div><span className="font-medium">Status:</span> {selectedChannel.status}</div>
                    <div><span className="font-medium">Priority:</span> {selectedChannel.priority}</div>
                    <div><span className="font-medium">Participants:</span> {selectedChannel.participants}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Activity Metrics</h4>
                  <div className="mt-2 space-y-2 text-sm">
                    <div><span className="font-medium">Total Messages:</span> {selectedChannel.messageCount}</div>
                    <div><span className="font-medium">Unread Messages:</span> {selectedChannel.unreadCount}</div>
                    <div><span className="font-medium">Last Activity:</span> {new Date(selectedChannel.lastActivity).toLocaleString()}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Channel Settings</h4>
                  <div className="mt-2 space-y-2 text-sm">
                    <div><span className="font-medium">Allow Replies:</span> {selectedChannel.settings.allowReplies ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Auto Archive:</span> {selectedChannel.settings.autoArchive ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Notification Level:</span> {selectedChannel.settings.notificationLevel}</div>
                    <div><span className="font-medium">Restricted Posting:</span> {selectedChannel.settings.restrictedPosting ? 'Yes' : 'No'}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Moderators</h4>
                  <div className="mt-2">
                    {selectedChannel.moderators.map(moderator => (
                      <span key={moderator} className="inline-block text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded mr-2 mb-2">
                        {moderator}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedChannel(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
                Manage Channel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collaboration Space Detail Modal */}
      {selectedSpace && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Workspace Details: {selectedSpace.name}</h3>
              <button
                onClick={() => setSelectedSpace(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Project Information</h4>
                  <div className="mt-2 space-y-2 text-sm">
                    <div><span className="font-medium">Type:</span> {selectedSpace.type}</div>
                    <div><span className="font-medium">Category:</span> {selectedSpace.category}</div>
                    <div><span className="font-medium">Status:</span> {selectedSpace.status}</div>
                    <div><span className="font-medium">Progress:</span> {selectedSpace.progress}%</div>
                    <div><span className="font-medium">Participants:</span> {selectedSpace.participants}</div>
                    <div><span className="font-medium">Lead:</span> {selectedSpace.lead}</div>
                    <div><span className="font-medium">Deadline:</span> {new Date(selectedSpace.deadline).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Available Features</h4>
                  <div className="mt-2 space-y-2 text-sm">
                    <div><span className="font-medium">Document Sharing:</span> {selectedSpace.features.documentSharing ? 'Enabled' : 'Disabled'}</div>
                    <div><span className="font-medium">Task Management:</span> {selectedSpace.features.taskManagement ? 'Enabled' : 'Disabled'}</div>
                    <div><span className="font-medium">Video Conferencing:</span> {selectedSpace.features.videoConferencing ? 'Enabled' : 'Disabled'}</div>
                    <div><span className="font-medium">Whiteboard:</span> {selectedSpace.features.whiteboardCollaboration ? 'Enabled' : 'Disabled'}</div>
                    <div><span className="font-medium">Real-time Editing:</span> {selectedSpace.features.realTimeEditing ? 'Enabled' : 'Disabled'}</div>
                  </div>
                </div>
                {selectedSpace.recentActivity && selectedSpace.recentActivity.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900">Recent Activity</h4>
                    <div className="mt-2 space-y-2">
                      {selectedSpace.recentActivity.slice(0, 3).map((activity, index) => (
                        <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                          <div className="font-medium">{activity.type}: {activity.item}</div>
                          <div className="text-gray-600">by {activity.user} • {new Date(activity.timestamp).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedSpace(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
                Enter Workspace
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ESGSupplierCommunicationHub;